import { useCallback } from 'react';
import axios from 'axios';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { reportUploadFailure } from 'error-reporting/error-reporting';
import { UploadAttempt, getTotalBytes, getUploadFailureFeedback, getUploadFailureKind } from 'utils/upload-failure';

type UploadFailureContext = {
    /** Which upload, for GlitchTip, e.g. 'order-files'. */
    upload: string;
    attempt: UploadAttempt;
    /** Resends the same files; offered as "Try again" in the dialog. */
    retry?: () => void;
};

/**
 * DD-3793: the catch block of a REST upload to denthub-documents. A cancelled upload is ignored,
 * a failed transfer gets the feedback dialog (with a retry when given) and a GlitchTip report
 * carrying its size and duration, and anything else goes to the error boundary as before.
 */
export const useUploadFailureHandler = () => {
    const { showBoundary, showUserFrontendFeedback } = useDenthubErrorBoundary();

    return useCallback(
        (error: unknown, { upload, attempt, retry }: UploadFailureContext) => {
            if (axios.isCancel(error)) return;
            const kind = getUploadFailureKind(error);
            if (!kind) {
                showBoundary(error);
                return;
            }
            reportUploadFailure(error, {
                upload,
                kind,
                fileCount: attempt.files.length,
                totalBytes: getTotalBytes(attempt.files),
                sentBytes: attempt.sentBytes,
                elapsedMs: Date.now() - attempt.startedAt,
            });
            // Sending a file that is too large again cannot help.
            const onRetry = kind === 'TOO_LARGE' ? undefined : retry;
            showUserFrontendFeedback({ ...getUploadFailureFeedback(kind, attempt.files), onRetry });
        },
        [showBoundary, showUserFrontendFeedback],
    );
};
